import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const pipelineService = {
  // Get all pipelines
  getAll: async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('pipelineService: Fetching all pipelines...');
    }

    const response = await api.get('/pipelines');

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'pipelineService', 'getAll');

    // Return the data directly (should be an array of pipelines)
    return apiResponse.data || [];
  },

  // Get pipeline by ID
  getById: async (id) => {
    const response = await api.get(`/pipelines/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'pipelineService', 'getById');

    // Return the data directly (should be a single pipeline object)
    return apiResponse.data;
  },

  // Create new pipeline
  create: async (pipelineData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelineService: Creating pipeline with data:', pipelineData);
      }

      // Create a copy of the pipelineData to avoid modifying the original
      const pipelineDataToSend = { ...pipelineData };

      const response = await api.post('/pipelines', pipelineDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'pipelineService', 'create');

      // Return the data directly (should be the created pipeline object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('pipelineService: Error creating pipeline');

      if (process.env.NODE_ENV === 'development') {
        console.error('pipelineService: Error details:', error);
        console.error('pipelineService: Error response data:', error.response?.data);
        console.error('pipelineService: Error status:', error.response?.status);
      }

      // Rethrow with more detailed error message
      if (error.response?.data) {
        const errorMessage = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
      }
      throw error;
    }
  },

  // Update pipeline
  update: async (id, pipelineData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelineService: Updating pipeline with ID:', id, 'and data:', pipelineData);
      }

      // Create a copy of the pipelineData to avoid modifying the original
      const pipelineDataToSend = { ...pipelineData };

      const response = await api.put(`/pipelines/${id}`, pipelineDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'pipelineService', 'update');

      // Return the data directly (should be the updated pipeline object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('pipelineService: Error updating pipeline');

      if (process.env.NODE_ENV === 'development') {
        console.error('pipelineService: Error details:', error);
        console.error('pipelineService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Delete pipeline
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelineService: Deleting pipeline with ID:', id);
      }

      const response = await api.delete(`/pipelines/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'pipelineService', 'remove');

      // For delete operations, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('pipelineService: Error deleting pipeline');

      if (process.env.NODE_ENV === 'development') {
        console.error('pipelineService: Error details:', error);
        console.error('pipelineService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },
};

export default pipelineService;
